import React, { useState } from "react";

import { useStripe, useElements, PaymentElement, Elements } from "@stripe/react-stripe-js";
import { FORM_ERROR } from "final-form";
import { Form } from "react-final-form";
import { useHistory } from "react-router";
import { stripePromise } from "Utils/Stripe";

import { usePaymentSetupIntentConfirmationMutation, usePaymentSetupIntentMutation } from "../api";
import { Body2 } from "Cobalt/BodyText";
import LoadingBar from "Cobalt/LoadingBar";
import AppearingElement from "Common/AppearingElement";
import { SaveButton } from "Common/forms/buttons";
import { Col, Row } from "Common/Grid";
import { Div } from "Common/HTML";

type PaymentElementLoadingState = "not_started" | "loading" | "loaded";
const SetupForm = () => {
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [paymentElementLoadingState, setPaymentElementLoadingState] = useState<PaymentElementLoadingState>("not_started");
  const [paymentSetupIntent] = usePaymentSetupIntentMutation();
  const [paymentSetupIntentConfirmation] = usePaymentSetupIntentConfirmationMutation();

  const onSubmit = async () => {
    if (!stripe) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Trigger form validation and wallet collection
    const { error: elementsSubmitError } = await elements.submit();
    if (elementsSubmitError) {
      // eslint-disable-next-line consistent-return
      return {
        [FORM_ERROR]: elementsSubmitError.message,
      };
    }

    const { error: paymentSetupIntentError, data: setupIntentData } = await paymentSetupIntent();
    if (paymentSetupIntentError) {
      // eslint-disable-next-line consistent-return
      return {
        [FORM_ERROR]: paymentSetupIntentError.data.message,
      };
    }

    const returnURL = `${window.location.origin}/api/v1/ben_admin_leads/payment_setup_intent_confirmation`;
    // Confirm the SetupIntent using the details collected by the Payment Element
    const confirmSetupResponse = await stripe.confirmSetup({
      elements,
      clientSecret: setupIntentData.clientSecret,
      confirmParams: {
        return_url: returnURL,
      },
      redirect: "if_required",
    });

    const { error: confirmSetupError } = confirmSetupResponse;
    if (confirmSetupError) {
      // This point is only reached if there's an immediate error when
      // confirming the setup. Show the error to your customer (for example, payment details incomplete)
      // handleError(error);
      // eslint-disable-next-line consistent-return
      return {
        [FORM_ERROR]: confirmSetupError.message,
      };
    }
    const { setupIntent } = confirmSetupResponse;
    const { error: paymentSetupIntentConfirmationError, data: setupIntentConfirmationData } = await paymentSetupIntentConfirmation(setupIntent);
    if (paymentSetupIntentConfirmationError) {
      // eslint-disable-next-line consistent-return
      return {
        [FORM_ERROR]: paymentSetupIntentConfirmationError.data.message,
      };
    }
    history.push(setupIntentConfirmationData.redirectPath);
    // Your customer is redirected to your `return_url`. For some payment
    // methods like iDEAL, your customer is redirected to an intermediate
    // site first to authorize the payment, then redirected to the `return_url`.
  };

  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <Row>
            <Col sm={12}>
              {paymentElementLoadingState === "not_started" && <LoadingBar ariaText="Loading payment widget" my={5} style={{ maxWidth: "100%" }} />}
              <PaymentElement onReady={() => setPaymentElementLoadingState("loaded")} onLoaderStart={() => setPaymentElementLoadingState("loading")} />
            </Col>
          </Row>
          <Row>
            <Col>
              <AppearingElement show={paymentElementLoadingState === "loaded"}>
                <Div className="flex justify-center items-center" mt={4}>
                  {submitError && <Body2 color="error">{submitError}</Body2>}
                  <SaveButton type="submit" disabled={!stripe} submitting={submitting} ml="auto" />
                </Div>
              </AppearingElement>
            </Col>
          </Row>
        </form>
      )}
    </Form>
  );
};
const elementsOptions = {
  mode: "setup",
  currency: "usd",
  // Fully customizable with appearance API.
  appearance: {
    /* ... */
  },
};
const StripeSetupIntentWidget = () => (
  <Elements stripe={stripePromise} options={elementsOptions}>
    <SetupForm />
  </Elements>
);

export default StripeSetupIntentWidget;
